import { SOCKET_URL } from "../config/backend";
import { useState, useEffect, useRef, useCallback } from "react";

import QuizPlayer from "../games/quiz/QuizPlayer";
import ScreenplayPlayer from "../games/screenplay/ScreenplayPlayer";
import SpyfallPlayer from "../games/spyfall/SpyfallPlayer";
import CarsPlayer from "../games/cars/CarsPlayer";
import DrawingPlayer from "../games/drawing/DrawingPlayer";
import TruthDarePlayer from "../games/truthdare/TruthDarePlayer";
import CrocodilePlayer from "../games/crocodile/CrocodilePlayer";
import QuiplashPlayer from "../games/quiplash/QuiplashPlayer";
import FibbagePlayer from "../games/fibbage/FibbagePlayer";
import TapracePlayer from "../games/taprace/TapracePlayer";
import StroopPlayer from "../games/stroop/StroopPlayer";
import FlappyPlayer from "../games/flappy/FlappyPlayer";
import DictatorPlayer from "../games/dictator/DictatorPlayer";
import TwoTruthsPlayer from "../games/twotruths/TwoTruthsPlayer";
import VotingPlayer from "../games/voting/VotingPlayer";
import MusicPlayer from "../games/music/MusicPlayer";
import EmojiMoviePlayer from "../games/emojimovie/EmojiMoviePlayer";
import FactOrFakePlayer from "../games/factorfake/FactOrFakePlayer";
import BumperPlayer from "../games/bumper/BumperPlayer";
import WheelWordPlayer from "../games/wheelword/WheelWordPlayer";
import SniperPlayer from "../games/sniper/SniperPlayer";
import JeopardyPlayer from "../games/jeopardy/JeopardyPlayer";
import ReactionPlayer from "../games/reaction/ReactionPlayer";
import SimonPlayer from "../games/simon/SimonPlayer";
import QuestPlayer from "../games/quest/QuestPlayer";
import Crewmate from "../components/Crewmate";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { Send, Eraser, Trophy, Crown, Home, Check, Flame, Smile, Timer } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = SOCKET_URL;

import { log } from "../hooks/useLogger";
export default function PlayerController() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectionFatal, setConnectionFatal] = useState(null);
  const [playerName, setPlayerName] = useState("");
  const [gameType, setGameType] = useState("quiz");
  const [gameState, setGameState] = useState("waiting"); // waiting, playing, finished
  const setGameStateWithHint = (s) => {
    setGameState(s);
    window.dispatchEvent(new Event(s === "playing" ? "bugreport-hide" : "bugreport-show"));
  };

  // Quiz state
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [spData, setSpData] = useState(null);
  const [spResult, setSpResult] = useState(null);
  const [spFinal, setSpFinal] = useState(null);

  // Spyfall state
  const [sfScreen, setSfScreen] = useState("waiting");
  const [sfRole, setSfRole] = useState(null);
  const [sfLocation, setSfLocation] = useState(null);
  const [sfRound, setSfRound] = useState(1);
  const [sfMaxRounds, setSfMaxRounds] = useState(3);
  const [sfQuestions, setSfQuestions] = useState([]);
  const [sfOthers, setSfOthers] = useState([]);
  const [sfAsker, setSfAsker] = useState("");
  const [sfCurrentQ, setSfCurrentQ] = useState("");
  const [sfLocationHint, setSfLocationHint] = useState(null);
  const [sfWaitingMsg, setSfWaitingMsg] = useState("");
  const [sfVoteOptions, setSfVoteOptions] = useState([]);
  const [sfAlreadyVoted, setSfAlreadyVoted] = useState(false);
  const [sfMyVote, setSfMyVote] = useState(null);
  const [sfGuessOptions, setSfGuessOptions] = useState([]);
  const [sfGuessEarly, setSfGuessEarly] = useState(false);
  const [sfResult, setSfResult] = useState(null);
  const [sfCanGuessEarly, setSfCanGuessEarly] = useState(false);
  const [sfEarlyPressed, setSfEarlyPressed] = useState(false);
  const [sfTimerSeconds, setSfTimerSeconds] = useState(20);
  const [carsOptions, setCarsOptions] = useState([]);
  const [carsSelected, setCarsSelected] = useState(null);
  const [carsResult, setCarsResult] = useState(null);
  const [quizOptions, setQuizOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizResult, setQuizResult] = useState(null);

  // Drawing state
  const [isDrawer, setIsDrawer] = useState(false);
  const [drawingWord, setDrawingWord] = useState("");
  const [drawingHint, setDrawingHint] = useState("");
  const [guess, setGuess] = useState("");
  const [canGuess, setCanGuess] = useState(true);
  const [revealedWord, setRevealedWord] = useState(null);

  // Voting state
  const [votingQuestion, setVotingQuestion] = useState(null);
  const [votingPlayers, setVotingPlayers] = useState([]);
  const [selectedVote, setSelectedVote] = useState(null);
  // Music
  const [musicOptions, setMusicOptions] = useState([]);
  const [musicSelected, setMusicSelected] = useState(null);
  const [musicResult, setMusicResult] = useState(null);
  const [musicTime, setMusicTime] = useState(30);
  const [musicHint, setMusicHint] = useState("");
  // Emoji Movie
  const [emojiOptions, setEmojiOptions] = useState([]);
  const [emojiSelected, setEmojiSelected] = useState(null);
  const [emojiResult, setEmojiResult] = useState(null);
  const [emojiTime, setEmojiTime] = useState(20);
  const [emojiEmoji, setEmojiEmoji] = useState("");
  // Fact or Fake
  const [fofFact, setFofFact] = useState("");
  const [fofSelected, setFofSelected] = useState(null); // true | false | null
  const [fofResult, setFofResult] = useState(null);
  const [fofTime, setFofTime] = useState(15);
  // Flappy Bird
  const [flappyState, setFlappyState] = useState("waiting"); // waiting | playing | dead | roundend
  const [flappyScore, setFlappyScore] = useState(0);
  const [flappyRoundEnd, setFlappyRoundEnd] = useState(null);
  const [flappyData, setFlappyData] = useState(null);

  // Диктатор
  const [dictPhase, setDictPhase] = useState("waiting"); // waiting | make_law | discussion | vote | result
  const [dictData, setDictData] = useState(null);
  const [dictLaw, setDictLaw] = useState("");
  const [dictVoteData, setDictVoteData] = useState(null);
  const [dictResult, setDictResult] = useState(null);
  const [dictVoted, setDictVoted] = useState(false);
  // 2 правды 1 ложь
  const [ttPhase, setTtPhase] = useState("waiting"); // waiting | input | voting | result
  const [ttData, setTtData] = useState(null);
  const [ttFacts, setTtFacts] = useState(["","",""]);
  const [ttLieIndex, setTtLieIndex] = useState(0);
  const [ttVoteData, setTtVoteData] = useState(null);
  const [ttVoteProgress, setTtVoteProgress] = useState(null);
  const [ttResult, setTtResult] = useState(null);
  const [ttVoted, setTtVoted] = useState(false);

  // Stroop — Не верь словам
  const [stroopQuestion, setStroopQuestion] = useState(null);
  const [stroopResult, setStroopResult] = useState(null);
  const [stroopLives, setStroopLives] = useState(3);
  const [stroopScore, setStroopScore] = useState(0);

  // Wheel of Words
  const [wwMyTurn, setWwMyTurn] = useState(false);
  const [wwPhase, setWwPhase] = useState("spin");       // spin | action | joker | waiting
  const [wwSpinResult, setWwSpinResult] = useState(null);
  const [wwSpinResultVisible, setWwSpinResultVisible] = useState(false);
  const [wwWordMask, setWwWordMask] = useState([]);
  const [wwGuessedLetters, setWwGuessedLetters] = useState([]);
  const [wwRoundScores, setWwRoundScores] = useState({});
  const [wwEliminated, setWwEliminated] = useState([]);
  const [wwCurrentPlayer, setWwCurrentPlayer] = useState(null);
  const [wwLetterInput, setWwLetterInput] = useState("");
  const [wwWordInput, setWwWordInput] = useState("");
  const [wwHint1Used, setWwHint1Used] = useState(false);
  const [wwHint2Used, setWwHint2Used] = useState(false);
  const [wwLetterResult, setWwLetterResult] = useState(null);
  const [wwWordResult, setWwWordResult] = useState(null);
  const [wwHint, setWwHint] = useState(null);
  const [wwRoundEnd, setWwRoundEnd] = useState(null);
  const [wwCategory, setWwCategory] = useState("");
  const [wwMode, setWwMode] = useState("letter");       // letter | word (action submode)

  // Sniper
  const [sniperTurn, setSniperTurn] = useState(null);
  const [sniperShot, setSniperShot] = useState(null);
  const [jeopardyBoard, setJeopardyBoard] = useState(null);
  const [jeopardyResult, setJeopardyResult] = useState(null);
  const [reactionPhase, setReactionPhase] = useState("waiting");
  const [reactionSignal, setReactionSignal] = useState(null);
  const [reactionRoundEnd, setReactionRoundEnd] = useState(null);
  const [reactionEarly, setReactionEarly] = useState(null);
  const [reactionRound, setReactionRound] = useState(1);
  const [reactionMaxRounds, setReactionMaxRounds] = useState(10);
  const [simonData, setSimonData] = useState(null);
  const [questData, setQuestData] = useState(null);

  // Bumper Cars
  const bumperRawRef = useRef({ health: 100, alive: true, timeLeft: 60 });
  const [bumperHealth, setBumperHealth] = useState(100);
  const [bumperTimeLeft, setBumperTimeLeft] = useState(60);
  const [bumperAlive, setBumperAlive] = useState(true);
  const [bumperEliminated, setBumperEliminated] = useState(null); // null | { by_player }
  const [bumperRoundInfo, setBumperRoundInfo] = useState(null);
  const [bumperRoundResult, setBumperRoundResult] = useState(null);
  // Card games
  const [bumperColor, setBumperColor] = useState("#9b5de5");
  const [myPlayerColor, setMyPlayerColor] = useState(sessionStorage.getItem("playerColor") || "#ff3cac");
  const [votingResult, setVotingResult] = useState(null);

  // Truth or Dare state
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [truthdareType, setTruthdareType] = useState(null);
  const [truthdareContent, setTruthdareContent] = useState(null);
  const [truthdareCompleted, setTruthdareCompleted] = useState(false);
  const [truthdareSkipped, setTruthdareSkipped] = useState(false);

  // Crocodile state
  const [crocodileIsExplainer, setCrocodileIsExplainer] = useState(false);
  const [crocodileWord, setCrocodileWord] = useState("");
  const [crocodileCategory, setCrocodileCategory] = useState("");
  const [crocodileExplainer, setCrocodileExplainer] = useState("");
  const [crocodileGuess, setCrocodileGuess] = useState("");
  const [crocodileTimeLeft, setCrocodileTimeLeft] = useState(60);
  const [crocodileGuessedBy, setCrocodileGuessedBy] = useState([]);
  const [crocodileRoundEnd, setCrocodileRoundEnd] = useState(null); // null | { word, guessed_by, time_expired, skipped }
  const [crocodileMyGuessCorrect, setCrocodileMyGuessCorrect] = useState(false);

  // Final results
  const [finalScores, setFinalScores] = useState([]);
  const [winner, setWinner] = useState(null);
  const [myScore, setMyScore] = useState(0);
  const [quiplashPrompt, setQuiplashPrompt] = useState(null);
  const [quiplashMyAnswer, setQuiplashMyAnswer] = useState(null);
  const [quiplashAnswered, setQuiplashAnswered] = useState(false);
  const [quiplashVoteData, setQuiplashVoteData] = useState(null);   // {prompt, answers}
  const [quiplashMyVote, setQuiplashMyVote] = useState(null);
  const [quiplashVoted, setQuiplashVoted] = useState(false);
  const [quiplashResult, setQuiplashResult] = useState(null);
  const [quiplashTimeLeft, setQuiplashTimeLeft] = useState(60);
  // Fibbage state
  const [fibbagePhase, setFibbagePhase] = useState("wait");
  const [fibbageQuestion, setFibbageQuestion] = useState(null);
  const [fibbageMyLie, setFibbageMyLie] = useState(null);
  const [fibbageOptions, setFibbageOptions] = useState(null);
  const [fibbageMyVote, setFibbageMyVote] = useState(null);
  const [fibbageMyIndex, setFibbageMyIndex] = useState(null); // индекс своей лжи
  const [fibbageResult, setFibbageResult] = useState(null);
  // Taprace state
  const [tapraceTrack, setTapraceTrack] = useState(null);
  const [tapracePos, setTapracePos] = useState(0);
  const [tapraceFinishPos, setTapraceFinishPos] = useState(1000);
  const [tapraceComboLevel, setTapraceComboLevel] = useState(0);
  const [tapraceMultiplier, setTapraceMultiplier] = useState(1);
  const [tapraceComboCount, setTapraceComboCount] = useState(0);
  const [tapraceFinished, setTapraceFinished] = useState(false);
  const [tapraceMyPlace, setTapraceMyPlace] = useState(null);
  const [tapraceRoundResult, setTapraceRoundResult] = useState(null);
  const [tapraceEvent, setTapraceEvent] = useState(null);
  const [tapraceRunning, setTapraceRunning] = useState(false);

  // Clear every game-specific screen when a new session starts or the host
  // restarts the room. Keeping this centralized prevents stale UI from a
  // previous game (result overlays, selected answers, timers, etc.) flashing
  // over the next game.
  const resetPlayerGameState = useCallback(() => {
    setQuizQuestion(null); setQuizOptions([]); setSelectedAnswer(null); setQuizResult(null);
    setCarsOptions([]); setCarsSelected(null); setCarsResult(null);
    setSpData(null); setSpResult(null); setSpFinal(null);
    setSfScreen("waiting"); setSfRole(null); setSfLocation(null); setSfRound(1); setSfMaxRounds(3);
    setSfQuestions([]); setSfOthers([]); setSfAsker(""); setSfCurrentQ(""); setSfLocationHint(null);
    setSfWaitingMsg(""); setSfVoteOptions([]); setSfAlreadyVoted(false); setSfMyVote(null);
    setSfGuessOptions([]); setSfGuessEarly(false); setSfResult(null); setSfCanGuessEarly(false);
    setSfEarlyPressed(false); setSfTimerSeconds(20);

    setIsDrawer(false); setDrawingWord(""); setDrawingHint(""); setGuess(""); setCanGuess(true); setRevealedWord(null);
    setVotingQuestion(null); setVotingPlayers([]); setSelectedVote(null); setVotingResult(null);
    setMusicOptions([]); setMusicSelected(null); setMusicResult(null); setMusicTime(30); setMusicHint("");
    setEmojiOptions([]); setEmojiSelected(null); setEmojiResult(null); setEmojiTime(20); setEmojiEmoji("");
    setFofFact(""); setFofSelected(null); setFofResult(null); setFofTime(15);
    setFlappyState("waiting"); setFlappyScore(0); setFlappyRoundEnd(null); setFlappyData(null);

    setDictPhase("waiting"); setDictData(null); setDictLaw(""); setDictVoteData(null); setDictResult(null); setDictVoted(false);
    setTtPhase("waiting"); setTtData(null); setTtFacts(["", "", ""]); setTtLieIndex(0);
    setTtVoteData(null); setTtVoteProgress(null); setTtResult(null); setTtVoted(false);
    setStroopQuestion(null); setStroopResult(null); setStroopLives(3); setStroopScore(0);

    setWwMyTurn(false); setWwPhase("spin"); setWwSpinResult(null); setWwSpinResultVisible(false);
    setWwWordMask([]); setWwGuessedLetters([]); setWwRoundScores({}); setWwEliminated([]);
    setWwCurrentPlayer(null); setWwLetterInput(""); setWwWordInput(""); setWwHint1Used(false); setWwHint2Used(false);
    setWwLetterResult(null); setWwWordResult(null); setWwHint(null); setWwRoundEnd(null); setWwCategory(""); setWwMode("letter");

    setSniperTurn(null); setSniperShot(null); setJeopardyBoard(null); setJeopardyResult(null);
    setReactionPhase("waiting"); setReactionSignal(null); setReactionRoundEnd(null); setReactionEarly(null);
    setReactionRound(1); setReactionMaxRounds(10); setSimonData(null); setQuestData(null);

    bumperRawRef.current = { health: 100, alive: true, timeLeft: 60 };
    setBumperHealth(100); setBumperTimeLeft(60); setBumperAlive(true); setBumperEliminated(null);
    setBumperRoundInfo(null); setBumperRoundResult(null);

    setIsMyTurn(false); setTruthdareType(null); setTruthdareContent(null); setTruthdareCompleted(false); setTruthdareSkipped(false);
    setCrocodileIsExplainer(false); setCrocodileWord(""); setCrocodileCategory(""); setCrocodileExplainer("");
    setCrocodileGuess(""); setCrocodileTimeLeft(60); setCrocodileGuessedBy([]); setCrocodileRoundEnd(null); setCrocodileMyGuessCorrect(false);

    setQuiplashPrompt(null); setQuiplashMyAnswer(null); setQuiplashAnswered(false); setQuiplashVoteData(null);
    setQuiplashMyVote(null); setQuiplashVoted(false); setQuiplashResult(null); setQuiplashTimeLeft(60);
    setFibbagePhase("wait"); setFibbageQuestion(null); setFibbageMyLie(null); setFibbageOptions(null);
    setFibbageMyVote(null); setFibbageMyIndex(null); setFibbageResult(null);
    setTapraceTrack(null); setTapracePos(0); setTapraceFinishPos(1000); setTapraceComboLevel(0);
    setTapraceMultiplier(1); setTapraceComboCount(0); setTapraceFinished(false); setTapraceMyPlace(null);
    setTapraceRoundResult(null); setTapraceEvent(null); setTapraceRunning(false);

    setFinalScores([]); setWinner(null); setMyScore(0);
  }, []);

  useEffect(() => {
    const storedName = sessionStorage.getItem("playerName");
    const storedRoom = sessionStorage.getItem("roomId");
    const storedGameType = sessionStorage.getItem("gameType");
    const storedColor = sessionStorage.getItem("playerColor");
    const playerTokenKey = storedName ? `playerToken:${roomId}:${storedName}` : null;

    if (!storedName || storedRoom !== roomId) {
      navigate("/join");
      return;
    }

    setPlayerName(storedName);
    if (storedGameType) setGameType(storedGameType);
    // Ensure color is applied from storage on mount
    if (storedColor) {
      setMyPlayerColor(storedColor);
      setBumperColor(storedColor);
    }

    const newSocket = io(BACKEND_URL, {
      // Start with HTTP polling (works through more mobile/ISP proxies), then
      // upgrade to WebSocket. tryAllTransports gives another fallback on failure.
      transports: ["polling", "websocket"],
      tryAllTransports: true,
      path: "/api/socket.io",
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,        // start fast
      reconnectionDelayMax: 3000,    // don't wait too long between attempts
      timeout: 10000,
    });

    newSocket.on("connect", () => {
      newSocket.emit("player_join", {
        room_id: roomId,
        player_name: storedName,
        player_token: playerTokenKey ? (sessionStorage.getItem(playerTokenKey) || "") : "",
      });
    });

    newSocket.on("disconnect", () => {
      setConnected(false);
    });

    newSocket.on("connect_error", () => {
      setConnected(false);
    });

    // iOS/Android: when user unlocks phone, visibilitychange fires.
    // If socket is disconnected at that point, force reconnect immediately.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (!newSocket.connected) {
          newSocket.connect();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    newSocket.on("host_disconnected", (data) => {
      const secs = data?.grace_seconds || 15;
      toast.error(`Хост отключился. Ждём ${secs} секунд...`, { duration: secs * 1000 });
    });

    newSocket.on("room_closed", (data) => {
      toast.error(data?.reason === "host_left" ? "Хост вышел. Комната закрыта." : "Комната закрыта.");
      sessionStorage.removeItem("roomId");
      sessionStorage.removeItem("gameType");
      sessionStorage.removeItem("playerColor");
      if (playerTokenKey) sessionStorage.removeItem(playerTokenKey);
      navigate("/");
    });

    // ── Bumper Cars ───────────────────────────────────────────────────────
    newSocket.on("bumper_round_start", (d) => {
      bumperRawRef.current = { health: 100, alive: true, timeLeft: d.time_seconds };
      setBumperHealth(100);
      setBumperTimeLeft(d.time_seconds);
      setBumperAlive(true);
      setBumperEliminated(null);
      setBumperRoundResult(null);
      setBumperRoundInfo({ num: d.round_num, total: d.total_rounds, name: d.round_name, emoji: d.round_emoji });
      if (d.cumulative_scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.cumulative_scores[n]??0); }
      setBumperColor(sessionStorage.getItem("playerColor") || "#9b5de5");
    });
    newSocket.on("bumper_round_end", (d) => {
      setBumperRoundResult(d);
      // Update live score after each round
      if (d.scores) {
        const myName = sessionStorage.getItem("playerName");
        if (myName && d.scores[myName] !== undefined) {
          setMyScore(d.scores[myName]);
        }
      }
    });

    // ── Flappy Bird ───────────────────────────────────────────────────────────
    newSocket.on("flappy_question", (d) => {
      setGameType("flappy");
      setGameStateWithHint("playing");
      setFlappyState("playing");
      setFlappyScore(0);
      setFlappyRoundEnd(null);
      setFlappyData(d);
    });
    newSocket.on("flappy_died", (d) => {
      const me = sessionStorage.getItem("playerName");
      if (d.name === me) {
        setFlappyState("dead");
        setFlappyScore(d.score);
      }
    });

    newSocket.on("flappy_round_end", (d) => {
      setFlappyRoundEnd(d);
      setFlappyState("roundend");
      if (d.total) { const n=sessionStorage.getItem("playerName"); if(n && d.total[n]!==undefined) setMyScore(d.total[n]); }
    });

    // ── Диктатор ─────────────────────────────────────────────────────────
    newSocket.on("dictator_question", (d) => {
      const me = sessionStorage.getItem("playerName");
      setDictData({...d, myPhase: d.dictator === me ? "make_law" : "waiting"});
      setDictResult(null); setDictVoted(false); setDictLaw("");
      setDictPhase(d.dictator === me ? "make_law" : "waiting");
      setGameType("dictator"); setGameStateWithHint("playing");
      if(d.scores && d.scores[me] !== undefined) setMyScore(d.scores[me]);
    });
    newSocket.on("dictator_law_set", (d) => {
      setDictData(prev => prev ? {...prev, phase:"discussion", law:d.law} : prev);
      setDictPhase("discussion");
    });
    newSocket.on("dictator_vote_start", (d) => {
      setDictVoteData(d); setDictVoted(Boolean(d.already_voted));
      const me = sessionStorage.getItem("playerName");
      setDictPhase(d.dictator === me ? "dictator_wait" : "vote");
    });
    newSocket.on("dictator_result", (d) => {
      setDictResult(d); setDictPhase("result");
      const n=sessionStorage.getItem("playerName"); if(n&&d.scores&&d.scores[n]!==undefined) setMyScore(d.scores[n]);
    });

    // ── 2 правды 1 ложь ──────────────────────────────────────────────────
    newSocket.on("two_truths_question", (d) => {
      const me = sessionStorage.getItem("playerName");
      setTtData(d); setTtResult(null); setTtVoted(false);
      setTtFacts(["","",""]); setTtLieIndex(0); setTtVoteData(null); setTtVoteProgress(null);
      setTtPhase(d.subject === me ? "input" : "waiting");
      setGameType("two_truths"); setGameStateWithHint("playing");
      if(d.scores && d.scores[me] !== undefined) setMyScore(d.scores[me]);
    });
    newSocket.on("two_truths_vote_start", (d) => {
      setTtVoteData(d); setTtVoted(Boolean(d.already_voted));
      const me = sessionStorage.getItem("playerName");
      setTtPhase(d.subject === me ? "subject_wait" : "voting");
    });
    newSocket.on("two_truths_vote_update", (d) => {
      setTtVoteProgress(d);
    });
    newSocket.on("two_truths_result", (d) => {
      setTtResult(d); setTtPhase("result");
      const n=sessionStorage.getItem("playerName"); if(n&&d.scores&&d.scores[n]!==undefined) setMyScore(d.scores[n]);
    });

    // ── Stroop ───────────────────────────────────────────────────────────
    newSocket.on("stroop_question", (d) => {
      setGameType("stroop"); setGameStateWithHint("playing");
      setStroopQuestion(d); setStroopResult(null);
      const me = sessionStorage.getItem("playerName");
      if (d.qnum === 1) { setStroopLives(3); setStroopScore(0); }
      if (me && d.lives && d.lives[me] !== undefined) setStroopLives(d.lives[me]);
      if (me && d.scores && d.scores[me] !== undefined) setStroopScore(d.scores[me]);
    });
    newSocket.on("stroop_result", (d) => {
      setStroopResult(d);
      const me = sessionStorage.getItem("playerName");
      if (me && d.lives && d.lives[me] !== undefined) setStroopLives(d.lives[me]);
      if (me && d.scores && d.scores[me] !== undefined) setStroopScore(d.scores[me]);
      if (me && d.scores && d.scores[me] !== undefined) setMyScore(d.scores[me]);
    });

    // ── Wheel of Words ───────────────────────────────────────────────────
    newSocket.on("wheelword_question", (d) => {
      setGameType("wheelword");
      setGameStateWithHint("playing");
      setWwCurrentPlayer(d.current_player);
      setWwWordMask(d.word_mask || []);
      setWwGuessedLetters(d.guessed_letters || []);
      setWwRoundScores(d.round_scores || {});
      setWwEliminated(d.eliminated || []);
      setWwCategory(d.category || "");
      setWwHint1Used(Boolean(d.hint1_used));
      setWwHint2Used(Boolean(d.hint2_used));
      setWwRoundEnd(null);
      setWwSpinResult(null);
      setWwSpinResultVisible(false);
      setWwLetterResult(null);
      setWwWordResult(null);
      setWwHint(null);
      // On reconnect the server sends the real current phase. Without this,
      // a player could see "spin" while the server is waiting for a letter.
      setWwPhase(d.phase || "spin");
    });

    newSocket.on("wheelword_turn", (d) => {
      setWwCurrentPlayer(d.current_player);
      setWwEliminated(d.eliminated || []);
      setWwGuessedLetters(d.guessed_letters || []);
      setWwRoundScores(d.round_scores || {});
      if (d.word_mask) setWwWordMask(d.word_mask);
      setWwSpinResult(null);
      setWwSpinResultVisible(false);
      setWwLetterResult(null);
      setWwWordResult(null);
      setWwHint(null);
      setWwPhase(d.phase || "spin");
    });

    newSocket.on("wheelword_tick", () => {});

    newSocket.on("wheelword_spin_result", (d) => {
      // Только sector_idx для анимации — результат придёт в spin_reveal
      setWwSpinResult(d);
      setWwSpinResultVisible(false);
    });
    newSocket.on("wheelword_spin_reveal", (d) => {
      // Колесо остановилось — показываем результат и активируем кнопки
      setWwSpinResult(d);
      setWwSpinResultVisible(true);
      const me = sessionStorage.getItem("playerName");
      if (d.player === me) {
        if (d.type === "bankrupt" || d.type === "skip") {
          setWwPhase("waiting");
        } else if (d.type === "joker") {
          setWwPhase("joker");
        } else {
          setWwPhase("action");
        }
      } else {
        setWwPhase("watching");
      }
    });

    newSocket.on("wheelword_letter_result", (d) => {
      setWwWordMask(d.word_mask || []);
      setWwGuessedLetters(d.guessed_letters || []);
      setWwRoundScores(d.round_scores || {});
      setWwLetterResult(d);
      // Фаза обновится через wheelword_turn который придёт следом
    });

    newSocket.on("wheelword_word_result", (d) => {
      setWwWordResult(d);
      if (d.player === sessionStorage.getItem("playerName") && !d.correct) {
        setWwPhase("waiting");
      }
    });

    newSocket.on("wheelword_hint_used", (d) => {
      setWwWordMask(d.word_mask || []);
      setWwGuessedLetters(d.guessed_letters || []);
      setWwRoundScores(d.round_scores || {});
      setWwHint(d);
      if (d.hint_type === "hint1") setWwHint1Used(true);
      if (d.hint_type === "hint2") setWwHint2Used(true);
    });

    newSocket.on("wheelword_round_end", (d) => {
      setWwRoundEnd(d);
      setWwPhase("waiting");
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });

    // ── Sniper ────────────────────────────────────────────────────────────
    newSocket.on("sniper_turn", (d) => {
      setGameType("sniper"); setGameStateWithHint("playing");
      setSniperTurn(d); setSniperShot(null);
    });
    newSocket.on("sniper_shot_result", (d) => {
      setSniperShot(d);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });

    // ── Simon ─────────────────────────────────────────────────────────────
    newSocket.on("simon_update", (d) => {
      setGameType("simon"); setGameStateWithHint("playing");
      setSimonData(d);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });

    // ── Quest ─────────────────────────────────────────────────────────────
    newSocket.on("quest_update", (d) => {
      setGameType("quest"); setGameStateWithHint("playing");
      setQuestData(d);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });
    newSocket.on("quest_votes_update", (d) => {
      setQuestData(prev => prev ? {
        ...prev,
        vote_counts:   d.vote_counts,
        total_voted:   d.total_voted,
        total_players: d.total_players,
      } : prev);
    });

    // ── Jeopardy ─────────────────────────────────────────────────────────
    newSocket.on("jeopardy_board", (d) => {
      setGameType("jeopardy"); setGameStateWithHint("playing");
      setJeopardyBoard(d);
      setJeopardyResult(null); // сбрасываем result при новом состоянии доски
      const n = sessionStorage.getItem("playerName");
      if (n && d.scores) setMyScore(d.scores[n] ?? 0);
    });
    newSocket.on("jeopardy_result", (d) => {
      setJeopardyResult(d);
      const n = sessionStorage.getItem("playerName");
      if (n && d.scores) setMyScore(d.scores[n] ?? 0);
      // Таймаут чуть меньше чем задержка на сервере (3.5с) — чтобы оверлей исчез до прихода board
      setTimeout(() => setJeopardyResult(null), 3200);
    });
    newSocket.on("jeopardy_timeout", (d) => {
      setJeopardyResult({ ...(d || {}), timeout: true });
      const n = sessionStorage.getItem("playerName");
      if (n && d?.scores) setMyScore(d.scores[n] ?? 0);
    });

    // ── Reaction ─────────────────────────────────────────────────────────
    newSocket.on("reaction_waiting", (d) => {
      setGameType("reaction"); setGameStateWithHint("playing");
      setReactionPhase("waiting"); setReactionSignal(null);
      setReactionRoundEnd(null); setReactionEarly(null);
      setReactionRound(d.round); setReactionMaxRounds(d.max_rounds);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });
    newSocket.on("reaction_signal", (d) => {
      setReactionPhase("signal"); setReactionSignal(d);
    });
    newSocket.on("reaction_round_end", (d) => {
      setReactionPhase("result"); setReactionRoundEnd(d);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
    });
    newSocket.on("reaction_early", (d) => {
      setReactionEarly(d);
      if (d.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(d.scores[n]??0); }
      setTimeout(() => setReactionEarly(null), 2000);
    });

        // ── Taprace ──────────────────────────────────────────────────────────
    newSocket.on("emoji_movie_question", (data) => {
      setGameType("emoji_movie");
      setGameStateWithHint("playing");
      setEmojiOptions(data.options || []);
      setEmojiEmoji(data.emoji || "");
      setEmojiTime(data.time_seconds || 20);
      setEmojiSelected(data.already_answered && data.my_answer !== undefined ? data.my_answer : null);
      setEmojiResult(null);
    });
    newSocket.on("emoji_movie_tick", (d) => { setEmojiTime(d.remaining); });
    newSocket.on("emoji_movie_result", (data) => { setEmojiResult(data); if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); } });

    newSocket.on("fact_or_fake_question", (data) => {
      setGameType("fact_or_fake");
      setGameStateWithHint("playing");
      setFofFact(data.fact || "");
      setFofTime(data.time_seconds || 30);
      setFofSelected(data.already_answered && data.my_answer !== undefined ? data.my_answer : null);
      setFofResult(null);
    });
    newSocket.on("fact_or_fake_tick", (d) => { setFofTime(d.remaining); });
    newSocket.on("fact_or_fake_result", (data) => { setFofResult(data); if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); } });

    newSocket.on("taprace_start", (d) => {
      setTapraceTrack(d.track);
      setTapraceFinishPos(d.finish_pos);
      setTapracePos(d.positions?.[storedName] || 0);
      setTapraceComboLevel(0); setTapraceMultiplier(1); setTapraceComboCount(0);
      setTapraceFinished(false); setTapraceMyPlace(null);
      setTapraceRoundResult(null); setTapraceEvent(null);
      setTapraceRunning(true);
      setGameStateWithHint("playing"); setGameType("taprace");
    });
    newSocket.on("taprace_tap_ack", (d) => {
      setTapracePos(d.pos);
      setTapraceComboLevel(d.combo_level);
      setTapraceMultiplier(d.multiplier);
      setTapraceComboCount(d.combo_count);
    });
    newSocket.on("taprace_player_finished", (d) => {
      if (d.player === storedName) {
        setTapraceFinished(true);
        setTapraceMyPlace(d.place);
        setTapraceRunning(false);
      }
    });
    newSocket.on("taprace_event", (d) => {
      if (d.player === storedName) {
        setTapraceEvent(d);
        setTimeout(() => setTapraceEvent(null), 3000);
      }
    });
    newSocket.on("taprace_round_end", (d) => {
      setTapraceRunning(false);
      setTapraceRoundResult(d);
      if (d.scores) {
        const myName = sessionStorage.getItem("playerName");
        if (myName && d.scores[myName] !== undefined) setMyScore(d.scores[myName]);
      }
    });

    // ── Quiplash ──────────────────────────────────────────────────────────
    newSocket.on("quiplash_prompt", (d) => {
      setQuiplashPrompt(d.prompt);
      setQuiplashMyAnswer(d.already_answered ? (d.my_answer||"") : null);
      setQuiplashAnswered(d.already_answered||false);
      setQuiplashVoteData(null);
      setQuiplashMyVote(null);
      setQuiplashVoted(false);
      setQuiplashResult(null);
      setQuiplashTimeLeft(d.time_seconds||60);
    });
    newSocket.on("quiplash_vote", (d) => {
      setQuiplashVoteData({prompt:d.prompt, answers:d.answers});
      setQuiplashMyVote(d.my_vote||null);
      setQuiplashVoted(!!(d.already_voted||d.my_vote));
      setQuiplashTimeLeft(d.time_seconds||30);
    });
    newSocket.on("quiplash_vote_result", (d) => {
      setQuiplashResult(d);
      setQuiplashVoteData(null);
      if (d.scores) {
        const myName = sessionStorage.getItem("playerName");
        if (myName && d.scores[myName] !== undefined) setMyScore(d.scores[myName]);
      }
    });
    // Fibbage listeners
    newSocket.on("fibbage_question", (d) => {
      setFibbagePhase(d.already_answered ? "wait" : "lie");
      setFibbageQuestion({prompt: d.prompt});
      setFibbageMyLie(d.already_answered ? (d.my_answer||"") : null);
      setFibbageOptions(null);
      setFibbageMyVote(null);
      setFibbageResult(null);
    });
    newSocket.on("fibbage_lie_submitted", (d) => {
      // Меняем фазу только у того кто отправил ложь
      const myName = sessionStorage.getItem("playerName");
      if (d.player === myName) {
        setFibbagePhase("wait");
      }
    });
    newSocket.on("fibbage_lie_error", (d) => {
      // Optimistic UI switches to waiting before the server validates the lie.
      // On rejection return the player to the input immediately instead of leaving
      // them stuck on "Ждём остальных" for the rest of the round.
      setFibbageMyLie(null);
      setFibbagePhase("lie");
      toast.error(d?.message || "Этот вариант нельзя использовать");
    });
    newSocket.on("fibbage_vote", (d) => {
      setFibbagePhase(d.already_voted ? "wait" : "vote");
      setFibbageOptions(d.options);
      setFibbageMyVote(d.already_voted ? (d.my_vote ?? null) : null);
      setFibbageMyIndex(d.my_index ?? null);
    });
    newSocket.on("fibbage_vote_error", (d) => {
      setFibbageMyVote(null);
      setFibbagePhase("vote");
      toast.error(d?.message || "Этот вариант выбрать нельзя");
    });
    newSocket.on("fibbage_reveal", (d) => {
      setFibbagePhase("result");
      setFibbageResult(d);
      setFibbageOptions(null);
      if (d.scores) {
        const myName = sessionStorage.getItem("playerName");
        if (myName && d.scores[myName] !== undefined) setMyScore(d.scores[myName]);
      }
    });


    newSocket.on("bumper_state", (d) => {
      const myName = sessionStorage.getItem("playerName");
      const mine = d.players?.find(p => p.name === myName);
      // d.time_left is server field name; always update regardless of mine
      bumperRawRef.current = {
        health: mine ? mine.health : bumperRawRef.current.health,
        alive: mine !== undefined ? mine.alive : bumperRawRef.current.alive,
        timeLeft: d.time_left ?? d.timeLeft ?? bumperRawRef.current.timeLeft,
      };
    });

    // bumper_eliminated removed — no elimination in new design
    // Players always stay active; score by damage dealt

    newSocket.on("player_joined", (data) => {
      setConnectionFatal(null);
      setConnected(true);
      setGameType(data.game_type);
      if (playerTokenKey && data.player_token) {
        sessionStorage.setItem(playerTokenKey, data.player_token);
      }
      // Save server-confirmed color (may differ from what client sent if invalid)
      if (data.player?.color) {
        sessionStorage.setItem("playerColor", data.player.color);
        setMyPlayerColor(data.player.color);
        setBumperColor(data.player.color);
      }
      if (data.rejoin) {
        // Rejoining mid-game — restore game state
        if (data.status === "playing") {
          setGameStateWithHint("playing");
        } else if (data.status === "finished") {
          setGameStateWithHint("finished");
        }
        // Reset ALL game state — backend will resend correct state via resend_game_state
        // Quiz
        setSelectedAnswer(null);
        setQuizResult(null);
        // Drawing
        setCanGuess(true);
        setRevealedWord(null);
        // Voting
        setSelectedVote(null);
        setVotingResult(null);
        // Music
        setMusicOptions([]);
        setMusicSelected(null);
        setMusicResult(null);
        setMusicTime(30);
        // Truth or Dare — reset so stale "completed" or wrong turn screen doesn't show
        setIsMyTurn(false);
        setTruthdareType(null);
        setTruthdareContent(null);
        setTruthdareCompleted(false);
        setTruthdareSkipped(false);
        // Crocodile — reset round end screen so stale "раунд окончен" doesn't show
        setCrocodileRoundEnd(null);
        setCrocodileGuessedBy([]);
        setCrocodileMyGuessCorrect(false);
        // Card games — will be restored via resend_game_state
        setSniperTurn(null); setSniperShot(null);
        toast("🔄 Переподключено!");
      } else {
        toast.success("Подключено!");
      }
    });

    newSocket.on("game_started", (data) => {
      if (!data?.rejoin) resetPlayerGameState();
      setGameStateWithHint("playing");
      setGameType(data.game_type);
    });

    // Quiz events
    newSocket.on("quiz_question", (data) => {
      if (data.logo) {
        setCarsOptions(data.options || []);
        setCarsSelected(data.already_answered ? data.my_answer : null);
        setCarsResult(null);
        setGameType("cars"); setGameStateWithHint("playing");
        return;
      }
      setQuizQuestion(data.question);
      setQuizOptions(data.options);
      setSelectedAnswer(data.already_answered ? data.my_answer : null);
      setQuizResult(null);
    });

    newSocket.on("screenplay_cards", (data) => { setSpData(data); setSpResult(null); setSpFinal(null); });
    newSocket.on("screenplay_phase_result", (data) => { setSpResult(data); });
    newSocket.on("screenplay_final", (data) => { setSpFinal(data); });

    // Spyfall
    newSocket.on("spyfall_role", (d) => {
      setSfRole(d.role);
      setSfLocation(d.role === "civilian" ? d.location : null);
      setSfRound(d.round);
      setSfMaxRounds(d.max_rounds);
      setSfScreen("role");
      setSfEarlyPressed(false);
      setSfCanGuessEarly(false);
      setSfAlreadyVoted(false);
      setSfMyVote(null);
      setSfResult(null);
    });
    newSocket.on("spyfall_choose_question", (d) => {
      setSfQuestions(d.questions);
      setSfOthers(d.others);
      setSfScreen("choose_question");
    });
    newSocket.on("spyfall_waiting", (d) => {
      setSfWaitingMsg(d.message || "Ожидание...");
      setSfScreen("waiting_msg");
    });
    newSocket.on("spyfall_answer_prompt", (d) => {
      setSfCurrentQ(d.question);
      setSfAsker(d.asker);
      setSfLocationHint(d.location_hint || null);
      setSfTimerSeconds(20);
      setSfScreen("answer_prompt");
    });
    newSocket.on("spyfall_spy_action", (d) => {
      setSfCanGuessEarly(d.can_guess);
      setSfScreen("discussion");
    });
    newSocket.on("spyfall_vote_prompt", (d) => {
      setSfVoteOptions(d.players || []);
      setSfAlreadyVoted(d.already_voted || false);
      setSfMyVote(null);
      setSfScreen("vote");
    });
    newSocket.on("spyfall_guess_location", (d) => {
      setSfGuessOptions(d.options || []);
      setSfGuessEarly(d.early || false);
      setSfTimerSeconds(d.seconds || 20);
      setSfScreen("guess_location");
    });
    newSocket.on("spyfall_round_result", (d) => {
      setSfResult(d);
      setSfScreen("result");
    });
    newSocket.on("quiz_result", (data) => {
      setCarsResult(data);
      setQuizResult(data);
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
      if (data.answers[storedName] === data.correct) {
        toast.success("+100 очков!");
      }
    });

    // Drawing events
    newSocket.on("drawing_start", (data) => {
      setIsDrawer(data.is_drawer);
      setDrawingWord(data.word || "");
      setDrawingHint(data.hint || "");
      setGuess("");
      // If rejoining and already guessed — keep canGuess=false so no duplicate
      setCanGuess(!data.already_guessed);
      setRevealedWord(null);
    });

    newSocket.on("correct_guess", (data) => {
      if (data.player_name === storedName) {
        toast.success("+100 очков!");
        setCanGuess(false);
      }
    });

    newSocket.on("drawing_end", (data) => {
      setRevealedWord(data.word);
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
    });

    // Voting events
    newSocket.on("voting_question", (data) => {
      setVotingQuestion(data.question);
      setVotingPlayers(data.players);
      // Restore vote if rejoining after having already voted
      setSelectedVote(data.my_vote || null);
      setVotingResult(null);
    });

    newSocket.on("voting_result", (data) => {
      setVotingResult(data);
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
      const myVotes = Object.values(data.votes).filter(v => v === storedName).length;
      if (myVotes > 0) {
        toast.success(`+${myVotes * 50} очков!`);
      }
    });

    // Music (обычный / реверс / быстрый — один обработчик)
    const onMusicQuestion = (data, type) => {
      if (type) { setGameType(type); setGameStateWithHint("playing"); }
      setMusicOptions(data.options || []);
      setMusicTime(data.time_seconds || 30);
      setMusicHint(data.hint || "");
      setMusicResult(null);
      setMusicSelected(data.already_answered && data.my_answer !== undefined ? data.my_answer : null);
    };
    newSocket.on("music_question",         (d) => onMusicQuestion(d, null));
    newSocket.on("reverse_music_question", (d) => onMusicQuestion(d, "reverse_music"));
    newSocket.on("fast_music_question",    (d) => onMusicQuestion(d, "fast_music"));
    newSocket.on("puzzle_music_question",  (d) => onMusicQuestion(d, "puzzle_music"));
    newSocket.on("music_tick", (data) => setMusicTime(data.remaining));
    newSocket.on("music_result", (data) => {
      setMusicResult(data);
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
      // Check if this player answered correctly using server-side answers
      const myName = sessionStorage.getItem("playerName");
      const myAnswer = data.answers?.[myName];
      if (myAnswer !== undefined && myAnswer !== null) {
        if (myAnswer === data.correct) {
          toast.success("✅ Правильно! +100 очков");
        } else {
          toast.error("❌ Неверно!");
        }
      }
    });

    newSocket.on("truthdare_turn", (data) => {
      setIsMyTurn(data.current_player === storedName);
      setTruthdareType(null);
      setTruthdareContent(null);
      setTruthdareCompleted(false);
      setTruthdareSkipped(false);
    });

    newSocket.on("truthdare_question", (data) => {
      setTruthdareType(data.type);
      setTruthdareContent(data.type === "truth" ? data.question : data.dare);
    });

    newSocket.on("truthdare_completed", (data) => {
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
      if (data.skipped) {
        setTruthdareSkipped(true);
        if (data.player === storedName) {
          toast.error("−25 очков 😬");
        }
      } else {
        setTruthdareCompleted(true);
        if (data.player === storedName) {
          toast.success("+50 очков! 🎉");
        }
      }
    });

    // Crocodile events
    newSocket.on("crocodile_start", (data) => {
      setCrocodileIsExplainer(data.is_explainer);
      setCrocodileWord(data.word || "");
      setCrocodileCategory(data.category || "");
      setCrocodileExplainer(data.explainer);
      setCrocodileTimeLeft(data.time_seconds);
      setCrocodileGuess("");
      setCrocodileGuessedBy([]);
      setCrocodileRoundEnd(null);
      setCrocodileMyGuessCorrect(false);
    });

    newSocket.on("crocodile_tick", (data) => {
      setCrocodileTimeLeft(data.remaining);
    });

    newSocket.on("crocodile_correct", (data) => {
      setCrocodileGuessedBy(prev => [...prev, data.player_name]);
      if (data.player_name === storedName) {
        setCrocodileMyGuessCorrect(true);
        toast.success("+100 очков!");
      } else {
        toast(`🐊 ${data.player_name} угадал!`);
      }
    });

    newSocket.on("crocodile_wrong", (data) => {
      if (data.player_name === storedName) {
        toast.error("Неверно, попробуй ещё!");
      }
    });

    newSocket.on("crocodile_end", (data) => {
      setCrocodileRoundEnd(data);
      if (data.scores) { const n=sessionStorage.getItem("playerName"); if(n) setMyScore(data.scores[n]??0); }
      if (data.guessed_by.includes(storedName)) {
        // already toasted on correct_guess
      } else if (!data.is_explainer) {
        // didn't guess it
      }
    });

    // Game end
    newSocket.on("game_ended", (data) => {
      setGameStateWithHint("finished");
      setFinalScores(data.final_scores);
      setWinner(data.winner);
      const myScoreEntry = data.final_scores.find(([name]) => name === storedName);
      if (myScoreEntry) setMyScore(myScoreEntry[1]);
    });

    newSocket.on("game_restarted", () => {
      resetPlayerGameState();
      setGameStateWithHint("waiting");
    });

    newSocket.on("error", (data) => {
      const message = data?.message || "Ошибка подключения";
      const fatal = message.includes("уже началась") || message.includes("имя уже занято")
        || message.includes("Комната не найдена") || message.includes("Недействительный токен");
      if (fatal) {
        setConnectionFatal(message);
        setConnected(false);
      }
      toast.error(message);
    });

    setSocket(newSocket);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      newSocket.disconnect();
    };
  }, [roomId, navigate, resetPlayerGameState]);

  // Sync bumper health/time display from ref at 200ms (avoids 20fps re-render storm)
  useEffect(() => {
    const iv = setInterval(() => {
      const r = bumperRawRef.current;
      setBumperHealth(h => { const n = Math.round(r.health); return Math.abs(n - h) >= 1 ? n : h; });
      setBumperTimeLeft(t => { const n = Math.round(r.timeLeft); return n !== t ? n : t; });
      if (!r.alive) setBumperAlive(false); // sync alive state from ref
    }, 200);
    return () => clearInterval(iv);
  }, []);


  // ── Wake Lock — не даём экрану гаснуть во время игры ──────────────────────
  useEffect(() => {
    if (!("wakeLock" in navigator)) return; // API не поддерживается — тихо пропускаем

    let wakeLock = null;

    async function requestWakeLock() {
      // Запрашиваем только если вкладка активна
      if (document.visibilityState !== "visible") return;
      try {
        wakeLock = await navigator.wakeLock.request("screen");
      } catch (e) {
        // Может упасть если браузер отказал (низкий заряд и т.д.) — не критично
      }
    }

    async function handleVisibilityChange() {
      // Wake Lock автоматически снимается при уходе вкладки в фон —
      // переподключаем когда пользователь возвращается
      if (document.visibilityState === "visible") {
        await requestWakeLock();
      }
    }

    requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {}); // тихо игнорируем если уже снят
        wakeLock = null;
      }
    };
  }, []);

  const sendCarsAnswer = (index) => {
    if (carsSelected !== null) return;
    setCarsSelected(index);
    if (socket) socket.emit("quiz_answer", { room_id: roomId, answer: index });
  };

  const sendAnswer = (index) => {
    if (socket && selectedAnswer === null) {
      setSelectedAnswer(index);
      socket.emit("quiz_answer", { room_id: roomId, answer: index });
    }
  };

  const sendGuess = () => {
    if (socket && guess.trim() && canGuess) {
      socket.emit("drawing_guess", { room_id: roomId, guess: guess.trim() });
      setGuess("");
    }
  };

  const sendVote = (votedFor) => {
    if (socket && selectedVote === null) {
      setSelectedVote(votedFor);
      socket.emit("vote", { room_id: roomId, voted_for: votedFor });
    }
  };

  const sendMusicAnswer = (idx) => {
    if (musicSelected !== null || musicResult) return; // already answered
    setMusicSelected(idx);
    if (socket) socket.emit("music_answer", { room_id: roomId, answer: idx });
  };

  const sendEmojiAnswer = (idx) => {
    if (emojiSelected !== null || emojiResult) return;
    setEmojiSelected(idx);
    if (socket) socket.emit("emoji_movie_answer", { room_id: roomId, answer: idx });
  };

  const sendFofAnswer = (val) => {
    if (fofSelected !== null || fofResult) return;
    setFofSelected(val);
    if (socket) socket.emit("fact_or_fake_answer", { room_id: roomId, answer: val });
  };

  const sendTruthdareChoice = (choice) => {
    if (socket) {
      socket.emit("truthdare_choice", { room_id: roomId, choice });
    }
  };

  const sendTruthdareComplete = () => {
    if (socket) {
      socket.emit("truthdare_complete", { room_id: roomId });
    }
  };

  const sendTruthdareSkip = () => {
    if (socket) {
      socket.emit("truthdare_skip", { room_id: roomId });
    }
  };

  const sendCrocodileGuess = () => {
    if (socket && crocodileGuess.trim() && !crocodileMyGuessCorrect && !crocodileRoundEnd) {
      socket.emit("crocodile_guess", { room_id: roomId, guess: crocodileGuess.trim() });
      setCrocodileGuess("");
    }
  };

  const sendCrocodileSkip = () => {
    if (socket) {
      socket.emit("crocodile_skip", { room_id: roomId });
    }
  };

  const leaveGame = () => {
    if (!window.confirm("Выйти из игры?")) return;
    const storedName = sessionStorage.getItem("playerName") || playerName;
    const tokenKey = storedName ? `playerToken:${roomId}:${storedName}` : null;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      sessionStorage.removeItem("roomId");
      sessionStorage.removeItem("gameType");
      sessionStorage.removeItem("playerColor");
      if (tokenKey) sessionStorage.removeItem(tokenKey);
      navigate("/join");
    };
    if (socket?.connected) {
      socket.emit("player_leave", { room_id: roomId }, finish);
      window.setTimeout(finish, 600);
    } else {
      finish();
    }
  };

  if (!connected) {
    return (
      <div className="min-h-screen bg-cartoon stars-bg flex items-center justify-center mobile-full">
        <div className="relative z-10 text-center animate-slide-up px-6 w-full max-w-xs">
          <div className="card-cartoon">
            {/* Spinning crewmate via CSS */}
            <div style={{
              width:"5rem",height:"5rem",
              border:"4px solid #9b5de5",borderTopColor:"transparent",
              borderRadius:"50%",animation:"spin-h .8s linear infinite",
              margin:"0 auto 1.25rem",
            }}/>
            <style>{`@keyframes spin-h{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
            <p className="font-display text-2xl text-white mb-1">{connectionFatal ? "Не удалось вернуться" : "Переподключение..."}</p>
            <p className="font-heading text-[#888] text-sm mb-4">
              {connectionFatal || "Не закрывай страницу — соединение восстанавливается"}
            </p>
            <div style={{
              background:"#261d60",border:"2px solid #9b5de5",borderRadius:".75rem",
              padding:".5rem .85rem",display:"inline-block",
            }}>
              <span className="font-display text-[#9b5de5] text-sm">
                🔌 Комната: {roomId}
              </span>
            </div>
            {connectionFatal && (
              <button
                className="btn-cartoon btn-secondary w-full mt-4 py-3"
                onClick={() => {
                  const name = sessionStorage.getItem("playerName") || playerName;
                  if (name) sessionStorage.removeItem(`playerToken:${roomId}:${name}`);
                  sessionStorage.removeItem("roomId");
                  sessionStorage.removeItem("gameType");
                  navigate(`/join?room=${roomId}`);
                }}
              >
                Вернуться ко входу
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cartoon stars-bg flex flex-col mobile-full">
      {/* Header */}
      <header style={{background:"#000",borderBottom:"3px solid #FF00FF",boxShadow:"0 4px 0 #000",padding:"0.6rem 1rem",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"0.5rem"}}>
        <button onClick={leaveGame}
          style={{background:"rgba(255,255,255,0.08)",border:"2px solid #444",borderRadius:"0.6rem",
            color:"#aaa",padding:"0.3rem 0.6rem",fontFamily:"'Fredoka One',sans-serif",
            fontSize:"0.8rem",cursor:"pointer",flexShrink:0}}>
          ← Выйти
        </button>
        <div className="text-center flex-1">
          <p className="font-display text-base text-[#FFFF00]">{roomId}</p>
        </div>
        <div className="text-right" style={{flexShrink:0}}>
          <p className="font-bold text-white text-sm">{playerName}</p>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col p-4" style={{overflowY:"auto",WebkitOverflowScrolling:"touch",minHeight:0,paddingBottom:"env(safe-area-inset-bottom,0.5rem)"}}>
        {gameState === "waiting" && (
          <WaitingController playerName={playerName} myColor={myPlayerColor} gameType={gameType}/>
        )}

        {gameState === "playing" && gameType === "screenplay" && (
          <ScreenplayPlayer
            data={spData}
            result={spResult}
            final={spFinal}
            onVote={(card) => socket?.emit("screenplay_vote", { room_id: roomId, card })}
          />
        )}

        {gameState === "playing" && gameType === "spyfall" && (
          <SpyfallPlayer
            screen={sfScreen}
            role={sfRole}
            location={sfLocation}
            round={sfRound}
            maxRounds={sfMaxRounds}
            questions={sfQuestions}
            others={sfOthers}
            asker={sfAsker}
            currentQ={sfCurrentQ}
            locationHint={sfLocationHint}
            waitingMsg={sfWaitingMsg}
            voteOptions={sfVoteOptions}
            alreadyVoted={sfAlreadyVoted}
            myVote={sfMyVote}
            guessOptions={sfGuessOptions}
            guessEarly={sfGuessEarly}
            result={sfResult}
            canGuessEarly={sfCanGuessEarly}
            earlyPressed={sfEarlyPressed}
            timerSeconds={sfTimerSeconds}
            playerName={playerName}
            onAsk={(q, target) => {
              socket?.emit("spyfall_ask", { room_id: roomId, question: q, target });
              setSfWaitingMsg("Ожидание ответа...");
              setSfScreen("waiting_msg");
            }}
            onAnswerDone={() => {
              socket?.emit("spyfall_answer_done", { room_id: roomId });
              setSfWaitingMsg("Следующий ход...");
              setSfScreen("waiting_msg");
            }}
            onVote={(suspect) => {
              setSfMyVote(suspect);
              setSfAlreadyVoted(true);
              socket?.emit("spyfall_vote", { room_id: roomId, suspect });
            }}
            onEarlyGuess={() => {
              setSfEarlyPressed(true);
              setSfCanGuessEarly(false);
              socket?.emit("spyfall_spy_guess_now", { room_id: roomId });
            }}
            onLocationGuess={(locId) => {
              socket?.emit("spyfall_location_guess", { room_id: roomId, location_id: locId });
              setSfWaitingMsg("Ожидаем результат...");
              setSfScreen("waiting_msg");
            }}
          />
        )}

        {gameState === "playing" && ["quiz","quiz_cinema","quiz_18","quiz_movies","lexicon"].includes(gameType) && (
          <QuizPlayer
            question={quizQuestion}
            options={quizOptions}
            selectedAnswer={selectedAnswer}
            result={quizResult}
            onAnswer={sendAnswer}
          />
        )}

        {gameState === "playing" && gameType === "cars" && (
          <CarsPlayer
            options={carsOptions}
            selectedAnswer={carsSelected}
            result={carsResult}
            onAnswer={sendCarsAnswer}
          />
        )}

                {gameState === "playing" && gameType === "drawing" && (
          <DrawingPlayer
            isDrawer={isDrawer}
            word={drawingWord}
            hint={drawingHint}
            roomId={roomId}
            socket={socket}
            guess={guess}
            setGuess={setGuess}
            onGuess={sendGuess}
            canGuess={canGuess}
            revealedWord={revealedWord}
          />
        )}

        {gameState === "playing" && (gameType === "music" || gameType === "reverse_music" || gameType === "fast_music") && (
          <MusicPlayer
            options={musicOptions}
            selected={musicSelected}
            result={musicResult}
            timeLeft={musicTime}
            hint={musicHint}
            isReverse={gameType === "reverse_music"}
            onAnswer={sendMusicAnswer}
          />
        )}
        {gameState === "playing" && gameType === "puzzle_music" && (
          <MusicPlayer
            options={musicOptions}
            selected={musicSelected}
            result={musicResult}
            timeLeft={musicTime}
            hint={musicHint || "🧩 Слушай отрывки на экране хоста!"}
            isReverse={false}
            onAnswer={sendMusicAnswer}
          />
        )}
        {gameState === "playing" && gameType === "emoji_movie" && (
          <EmojiMoviePlayer
            emoji={emojiEmoji}
            options={emojiOptions}
            selected={emojiSelected}
            result={emojiResult}
            timeLeft={emojiTime}
            onAnswer={sendEmojiAnswer}
          />
        )}

        {gameState === "playing" && gameType === "fact_or_fake" && (
          <FactOrFakePlayer
            fact={fofFact}
            selected={fofSelected}
            result={fofResult}
            timeLeft={fofTime}
            onAnswer={sendFofAnswer}
          />
        )}

        {(gameState === "playing" || flappyState === "roundend") && gameType === "flappy" && (
          <FlappyPlayer
            data={flappyData}
            state={flappyState}
            score={flappyScore}
            roundEnd={flappyRoundEnd}
            playerName={sessionStorage.getItem("playerName") || playerName}
            onDied={(pipes) => socket && socket.emit("flappy_died", { room_id: roomId, score: pipes })}
            onPos={(pos) => socket && socket.emit("flappy_pos", { room_id: roomId, ...pos })}
          />
        )}

        {gameState === "playing" && gameType === "dictator" && (
          <DictatorPlayer
            phase={dictPhase}
            data={dictData}
            law={dictLaw}
            setLaw={setDictLaw}
            voteData={dictVoteData}
            result={dictResult}
            voted={dictVoted}
            setVoted={setDictVoted}
            onSubmitLaw={() => socket?.emit("dictator_set_law", { room_id: roomId, law: dictLaw.trim() })}
            onVote={(accused) => socket?.emit("dictator_vote", { room_id: roomId, accused })}
            onEndDiscussion={() => socket?.emit("dictator_end_discussion", { room_id: roomId })}
          />
        )}
        {gameState === "playing" && gameType === "two_truths" && (
          <TwoTruthsPlayer
            phase={ttPhase}
            data={ttData}
            facts={ttFacts}
            setFacts={setTtFacts}
            lieIndex={ttLieIndex}
            setLieIndex={setTtLieIndex}
            voteData={ttVoteData}
            voteProgress={ttVoteProgress}
            result={ttResult}
            voted={ttVoted}
            setVoted={setTtVoted}
            onSubmit={() => socket?.emit("two_truths_submit", {
              room_id: roomId,
              facts: ttFacts.map(f => f.trim()),
              lie_index: ttLieIndex,
            })}
            onVote={(idx) => socket?.emit("two_truths_vote", { room_id: roomId, fact_index: idx })}
          />
        )}
        {gameState === "playing" && gameType === "stroop" && (
          <StroopPlayer
            question={stroopResult ? null : stroopQuestion}
            result={stroopResult}
            myLives={stroopLives}
            myScore={stroopScore}
            playerName={sessionStorage.getItem("playerName") || ""}
            onAnswer={(colorId) => socket && socket.emit("stroop_answer", { room_id: roomId, color_id: colorId })}
          />
        )}

        {gameState === "playing" && gameType === "wheelword" && (
          <WheelWordPlayer
            phase={wwPhase}
            spinResult={wwSpinResult}
            spinResultVisible={wwSpinResultVisible}
            wordMask={wwWordMask}
            guessedLetters={wwGuessedLetters}
            roundScores={wwRoundScores}
            eliminated={wwEliminated}
            currentPlayer={wwCurrentPlayer}
            hint1Used={wwHint1Used}
            hint2Used={wwHint2Used}
            letterResult={wwLetterResult}
            wordResult={wwWordResult}
            hint={wwHint}
            roundEnd={wwRoundEnd}
            category={wwCategory}
            playerName={sessionStorage.getItem("playerName") || playerName}
            onSpin={() => socket && socket.emit("wheelword_spin", { room_id: roomId })}
            onGuessLetter={(l) => socket && socket.emit("wheelword_guess_letter", { room_id: roomId, letter: l })}
            onGuessWord={(w) => socket && socket.emit("wheelword_guess_word", { room_id: roomId, word: w })}
            onJokerPick={(l) => socket && socket.emit("wheelword_joker_pick", { room_id: roomId, letter: l })}
            onHint={(t) => socket && socket.emit("wheelword_use_hint", { room_id: roomId, hint_type: t })}
            onPass={() => socket && socket.emit("wheelword_pass", { room_id: roomId })}
          />
        )}

        {gameState === "playing" && gameType === "sniper" && (
          <SniperPlayer
            turnData={sniperTurn}
            shotResult={sniperShot}
            playerName={sessionStorage.getItem("playerName") || playerName}
            onFire={(pos) => socket && socket.emit("sniper_fire", { room_id: roomId, pos })}
          />
        )}

        {gameState === "playing" && gameType === "simon" && (
          <SimonPlayer
            simonData={simonData}
            playerName={sessionStorage.getItem("playerName") || playerName}
            onPress={(button) => socket && socket.emit("simon_press", { room_id: roomId, button })}
          />
        )}

        {gameState === "playing" && gameType === "quest" && (
          <QuestPlayer
            questData={questData}
            playerName={sessionStorage.getItem("playerName") || playerName}
            onVote={(choiceIndex) => socket && socket.emit("quest_vote", { room_id: roomId, choice_index: choiceIndex })}
          />
        )}

        {gameState === "playing" && gameType === "jeopardy" && (
          !jeopardyBoard
            ? <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flex:1,gap:12}}>
                <div style={{fontFamily:"'Fredoka One',sans-serif",color:"#ffe600",fontSize:"1.3rem"}}>🏆 Своя игра</div>
                <div style={{fontFamily:"'Nunito',sans-serif",color:"#aaa",fontSize:"0.9rem"}}>Загружаем...</div>
              </div>
            : <JeopardyPlayer
                board={jeopardyBoard}
                result={jeopardyResult}
                playerName={sessionStorage.getItem("playerName") || playerName}
                onPick={(catIdx, value) => socket && socket.emit("jeopardy_pick", { room_id: roomId, cat_idx: catIdx, value })}
                onBuzz={() => socket && socket.emit("jeopardy_buzz", { room_id: roomId })}
                onAnswer={(answer) => socket && socket.emit("jeopardy_answer", { room_id: roomId, answer })}
              />
        )}

        {gameState === "playing" && gameType === "reaction" && (
          <ReactionPlayer
            phase={reactionPhase}
            signal={reactionSignal}
            roundEnd={reactionRoundEnd}
            early={reactionEarly}
            myName={sessionStorage.getItem("playerName") || playerName}
            scores={{}}
            round={reactionRound}
            maxRounds={reactionMaxRounds}
            onPress={() => socket && socket.emit("reaction_press", { room_id: roomId })}
          />
        )}

        {gameState === "playing" && gameType === "bumper" && (
          <BumperPlayer
            socket={socket}
            roomId={roomId}
            health={bumperHealth}
            timeLeft={bumperTimeLeft}
            alive={bumperAlive}
            eliminated={bumperEliminated}
            myColor={bumperColor}
            roundInfo={bumperRoundInfo}
            roundResult={bumperRoundResult}
            myScore={myScore}
          />
        )}
        {gameState === "playing" && gameType === "voting" && (
          <VotingPlayer
            question={votingQuestion}
            players={votingPlayers}
            selectedVote={selectedVote}
            result={votingResult}
            playerName={playerName}
            onVote={sendVote}
          />
        )}

        {gameState === "playing" && gameType === "truthdare" && (
          <TruthDarePlayer
            isMyTurn={isMyTurn}
            type={truthdareType}
            content={truthdareContent}
            completed={truthdareCompleted}
            skipped={truthdareSkipped}
            onChoice={sendTruthdareChoice}
            onComplete={sendTruthdareComplete}
            onSkip={sendTruthdareSkip}
          />
        )}

        {gameType === "quiplash" && (gameState === "playing" || quiplashPrompt !== null) && (
          <QuiplashPlayer
            prompt={quiplashPrompt}
            myAnswer={quiplashMyAnswer}
            answered={quiplashAnswered}
            voteData={quiplashVoteData}
            myVote={quiplashMyVote}
            voted={quiplashVoted}
            result={quiplashResult}
            timeLeft={quiplashTimeLeft}
            playerName={playerName}
            onAnswer={(text) => {
              setQuiplashMyAnswer(text);
              setQuiplashAnswered(true);
              socket.emit("quiplash_answer", { room_id: roomId, text });
            }}
            onVote={(votedFor) => {
              setQuiplashMyVote(votedFor);
              setQuiplashVoted(true);
              socket.emit("quiplash_vote", { room_id: roomId, voted_for: votedFor });
            }}
          />
        )}
        {gameType === "fibbage" && gameState === "playing" && (
          <FibbagePlayer
            phase={fibbagePhase}
            question={fibbageQuestion}
            options={fibbageOptions}
            result={fibbageResult}
            myLie={fibbageMyLie}
            myVote={fibbageMyVote}
            myIndex={fibbageMyIndex}
            playerColor={myPlayerColor}
            playerName={playerName}
            onSubmitLie={(text) => {
              setFibbageMyLie(text);
              setFibbagePhase("wait");
              socket.emit("fibbage_lie", { room_id: roomId, text });
            }}
            onSubmitVote={(index) => {
              setFibbageMyVote(index);
              socket.emit("fibbage_vote_cast", { room_id: roomId, index });
            }}
          />
        )}
        {gameType === "taprace" && (tapraceRunning || tapraceFinished || tapraceRoundResult) && (
          <TapracePlayer
            track={tapraceTrack}
            pos={tapracePos}
            finishPos={tapraceFinishPos}
            comboLevel={tapraceComboLevel}
            multiplier={tapraceMultiplier}
            comboCount={tapraceComboCount}
            finished={tapraceFinished}
            myPlace={tapraceMyPlace}
            roundResult={tapraceRoundResult}
            event={tapraceEvent}
            playerName={playerName}
            onTap={() => socket?.emit("taprace_tap", { room_id: roomId })}
          />
        )}
        {gameState === "playing" && gameType === "crocodile" && (
          <CrocodilePlayer
            isExplainer={crocodileIsExplainer}
            word={crocodileWord}
            category={crocodileCategory}
            explainer={crocodileExplainer}
            guess={crocodileGuess}
            setGuess={setCrocodileGuess}
            timeLeft={crocodileTimeLeft}
            guessedBy={crocodileGuessedBy}
            myGuessCorrect={crocodileMyGuessCorrect}
            roundEnd={crocodileRoundEnd}
            onGuess={sendCrocodileGuess}
            onSkip={sendCrocodileSkip}
            playerName={playerName}
          />
        )}

        {gameState === "finished" && (
          <ResultsController
            finalScores={finalScores}
            winner={winner}
            playerName={playerName}
            myScore={myScore}
            onHome={() => navigate("/")}
          />
        )}
      </main>
    </div>
  );
}

function WaitingController({ playerName, myColor, gameType }) {
  const COLORS = ["#ff3cac","#00f5ff","#ffe600","#3dffa0","#ff8c00","#9b5de5","#ff3355"];
  const color = myColor || (playerName
    ? COLORS[playerName.split("").reduce((a,c)=>a+c.charCodeAt(0),0) % COLORS.length]
    : "#ff3cac");
  const rule = PC_GAME_RULES[gameType];
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 animate-slide-up">
      <div className="flex justify-center">
        <Crewmate color={color} size={90} animate="bounce"/>
      </div>
      <h2 className="font-display text-3xl text-white">Ты в игре!</h2>
      <p className="font-heading text-[#aaa]">Ожидай начала игры...</p>
      {rule && (
        <div className="card-cartoon w-full max-w-xs" style={{padding:"0.75rem 1rem",marginTop:"0.5rem"}}>
          <p className="font-heading text-[#888] text-xs uppercase mb-1">📋 Правила</p>
          <p className="font-body text-[#ccc] text-sm leading-snug">{rule}</p>
        </div>
      )}
    </div>
  );
}

function ResultsController({ finalScores, winner, playerName, myScore, onHome }) {
  const myPosition = finalScores.findIndex(([name]) => name === playerName) + 1;
  const isWinner = winner === playerName;

  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center animate-slide-up">
      {isWinner ? (
        <>
          <div className="flex justify-center mb-3">
            <Crewmate color="#ffe600" size={90} animate="bounce" crown/>
          </div>
          <h2 className="font-display text-3xl text-[#ffe600] neon-yellow mb-2">ТЫ ПОБЕДИЛ!</h2>
        </>
      ) : (
        <>
          <div className="flex justify-center mb-3">
            <Crewmate color="#9b5de5" size={80} animate="bounce"/>
          </div>
          <h2 className="font-display text-2xl text-white mb-2">Игра окончена</h2>
        </>
      )}

      <div className="card-cartoon mb-6 text-center">
        <p className="text-[#A1A1AA] text-sm mb-2">ТВОЙ РЕЗУЛЬТАТ</p>
        <p className="font-display text-4xl text-[#FF00FF]">{myScore}</p>
        <p className="text-[#A1A1AA]">Место: {myPosition} из {finalScores.length}</p>
      </div>

      <button
        onClick={onHome}
        data-testid="controller-home-btn"
        className="btn-cartoon btn-secondary px-8 py-4"
      >
        <Home className="w-5 h-5 inline mr-2" />
        На главную
      </button>
    </div>
  );
}
const PC_GAME_RULES = {
  quiz:      "Вопрос с 4 вариантами — жми быстрее! Скорость = очки.",
  drawing:   "Угадай что рисуют! Или рисуй сам — кто быстрее угадает.",
  voting:    "Голосуй за игрока в вопросе «Кто из вас…?»",
  truthdare: "Выбери: Правда или Действие. Пропуск = -25 очков.",
  crocodile: "Угадай слово по жестам! Без подсказок словами.",
  music:     "Слушай песню и выбери правильный вариант из 4.",
  bumper:    "Тарань соперников джойстиком! Наноси урон = зарабатывай очки.",
  durak:     "Атакуй и отбивайся. Сбрось все карты — не дурак!",
  uno:       "Сбрось все карты. 1 карта — жми УНО! Иначе штраф +2.",
  quiz_cinema: "Вопросы про кино! Выбирай правильный ответ из 4. Быстрее = больше очков.",
  quiz_18:     "Вопросы 18+. Выбирай правильный ответ из 4. Только для взрослых!",
  quiplash:    "Придумай смешной ответ на фразу! 60 сек. Потом голосуем — 100 очков за каждый голос.",
  taprace:     "ЖМИ! ЖМИ! ЖМИ! Серии тапов дают ×1.5, ×2, ×3 скорость. Первый до финиша — победитель раунда!",
};

// ════════════════════════════════════════════════════════════════════
// ── ДУРАК CONTROLLER ────────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════

const SUIT_COLOR_PC = {"♠":"#111","♣":"#111","♥":"#cc0000","♦":"#cc0000"};
const UNO_BG = {red:"#E53935",yellow:"#FDD835",green:"#43A047",blue:"#1E88E5",wild:"#7B1FA2"};